import { useEffect, useState } from "react";

/* Fetches the full BSL signs library (~485 signs) from a same-origin
   JSON snapshot at /data/bsl-signs.json and caches the result in
   sessionStorage so subsequent visits are instant.

   We snapshot the WP feed into /public/data/ at build time because
   the live feed at british-sign.co.uk does not return CORS headers
   so the browser cannot fetch it cross-origin. Refresh the snapshot
   periodically when new signs are added.

   Returns { signs, status, error }. */

const FEED_URL = "/data/bsl-signs.json";
const CACHE_KEY = "bsl-signs-feed-v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.signs;
  } catch (e) {
    return null;
  }
};

const writeCache = (signs) => {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ cachedAt: Date.now(), signs })
    );
  } catch (e) {
    /* sessionStorage may be full; ignore */
  }
};

export const useBslSigns = () => {
  const [signs, setSigns] = useState(() => readCache() || []);
  const [status, setStatus] = useState(() =>
    readCache() ? "ready" : "loading"
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (status === "ready") return;
    const controller = new AbortController();
    let cancelled = false;

    fetch(FEED_URL, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled) return;
        if (!data || !Array.isArray(data.signs)) {
          throw new Error("Unexpected response shape");
        }
        // sort alphabetically for predictable search results
        const sorted = data.signs
          .filter((s) => s.name && s.image_url)
          .sort((a, b) => a.name.localeCompare(b.name));
        setSigns(sorted);
        setStatus("ready");
        writeCache(sorted);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || String(e));
        setStatus("error");
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [status]);

  return { signs, status, error };
};

export default useBslSigns;
